"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '../../components/accordion'
import { UIBox } from './ui-box'

// ========================================
// Base Components (shadcn/ui 패턴 그대로 노출)
// ========================================

/**
 * UIAccordion - 기본 아코디언 컨테이너
 *
 * shadcn/ui Accordion을 그대로 노출하여 최대한의 유연성을 제공합니다.
 *
 * @example
 * ```tsx
 * <UIAccordion type="single" collapsible>
 *   <UIAccordionItem value="item-1">
 *     <UIAccordionTrigger>트리거</UIAccordionTrigger>
 *     <UIAccordionContent>콘텐츠</UIAccordionContent>
 *   </UIAccordionItem>
 * </UIAccordion>
 * ```
 */
export const UIAccordion = React.forwardRef<
  React.ComponentRef<typeof Accordion>,
  React.ComponentPropsWithoutRef<typeof Accordion>
>((props, ref) => {
  return <Accordion ref={ref} {...props} />
})

UIAccordion.displayName = "UIAccordion"

/**
 * UIAccordionItem - 개별 아코디언 아이템
 */
export const UIAccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionItem>,
  React.ComponentPropsWithoutRef<typeof AccordionItem>
>((props, ref) => {
  return <AccordionItem ref={ref} {...props} />
})

UIAccordionItem.displayName = "UIAccordionItem"

/**
 * UIAccordionTrigger - 아코디언 트리거 (클릭 가능한 헤더)
 */
export const UIAccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionTrigger>,
  React.ComponentPropsWithoutRef<typeof AccordionTrigger>
>((props, ref) => {
  return <AccordionTrigger ref={ref} {...props} />
})

UIAccordionTrigger.displayName = "UIAccordionTrigger"

/**
 * UIAccordionContent - 아코디언 콘텐츠 (접히고 펼쳐지는 영역)
 */
export const UIAccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionContent>,
  React.ComponentPropsWithoutRef<typeof AccordionContent>
>((props, ref) => {
  return <AccordionContent ref={ref} {...props} />
})

UIAccordionContent.displayName = "UIAccordionContent"

// ========================================
// Preset Components (특별한 용도에 최적화)
// ========================================

const accordionPresetVariants = cva(
  "w-full",
  {
    variants: {
      variant: {
        default: "border border-border rounded-lg overflow-hidden bg-background",
        ghost: "space-y-1",
        outline: "space-y-2",
        contained: "border border-border rounded-lg overflow-hidden bg-muted/30",
      },
      size: {
        sm: "text-sm",
        default: "",
        lg: "text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface AccordionItemData {
  /**
   * 아코디언 아이템의 고유 식별자
   */
  id: string
  /**
   * 트리거 버튼에 표시될 내용
   */
  trigger: React.ReactNode
  /**
   * 펼쳤을 때 표시될 콘텐츠
   */
  content: React.ReactNode
  /**
   * 아이템 비활성화 여부
   */
  disabled?: boolean
  /**
   * 아이템별 추가 CSS 클래스
   */
  className?: string
}

export interface UIAccordionFAQProps extends VariantProps<typeof accordionPresetVariants> {
  /**
   * FAQ 아이템들
   */
  items: AccordionItemData[]
  /**
   * 아코디언 타입
   */
  type?: 'single' | 'multiple'
  /**
   * single 타입에서 닫기 가능 여부
   */
  collapsible?: boolean
  /**
   * 기본으로 열려있을 아이템
   */
  defaultValue?: string | string[]
  /**
   * 값 변경 콜백
   */
  onValueChange?: (value: string | string[]) => void
  /**
   * 추가 CSS 클래스
   */
  className?: string
}

/**
 * UIAccordionFAQ - FAQ에 최적화된 프리셋 아코디언
 *
 * 자주 묻는 질문 페이지나 도움말 섹션에 사용하기 적합한 스타일링이 적용되어 있습니다.
 *
 * @example
 * ```tsx
 * const faqItems = [
 *   {
 *     id: 'q1',
 *     trigger: 'Q: 계정을 어떻게 만들 수 있나요?',
 *     content: 'A: 회원가입 페이지에서 이메일과 비밀번호를 입력하여 계정을 만들 수 있습니다.'
 *   }
 * ]
 *
 * <UIAccordionFAQ items={faqItems} type="single" collapsible />
 * ```
 */
export const UIAccordionFAQ = React.forwardRef<
  React.ComponentRef<typeof UIAccordion>,
  UIAccordionFAQProps
>((
  {
    items,
    type = "single",
    collapsible = true,
    defaultValue,
    onValueChange,
    variant = "default",
    size = "default",
    className,
    ...props
  },
  ref
) => {
  // 타입에 따른 적절한 props 설정
  const accordionProps = React.useMemo(() => {
    const baseProps: any = {
      type,
      ...props
    }

    if (type === 'single') {
      baseProps.collapsible = collapsible
      if (defaultValue && typeof defaultValue === 'string') {
        baseProps.defaultValue = defaultValue
      }
      if (onValueChange) {
        baseProps.onValueChange = onValueChange
      }
    } else {
      if (defaultValue && Array.isArray(defaultValue)) {
        baseProps.defaultValue = defaultValue
      }
      if (onValueChange) {
        baseProps.onValueChange = onValueChange
      }
    }

    return baseProps
  }, [type, collapsible, defaultValue, onValueChange, props])

  return (
    <UIAccordion
      ref={ref}
      className={cn(
        accordionPresetVariants({ variant, size }),
        className
      )}
      {...accordionProps}
    >
      {items.map((item) => (
        <UIAccordionItem
          key={item.id}
          value={item.id}
          disabled={item.disabled}
          className={cn(
            // Base item styling
            "group",
            // Variant-specific styling
            variant === "default" && "border-b border-border last:border-b-0",
            variant === "ghost" && "border-b border-border/50 last:border-b-0 hover:bg-muted/50 transition-colors",
            variant === "outline" && "border border-border rounded-lg bg-background hover:bg-muted/30 transition-colors",
            variant === "contained" && "border-b border-border/30 last:border-b-0 hover:bg-muted/50 transition-colors",
            item.className
          )}
        >
          <UIAccordionTrigger
            className={cn(
              // Base trigger styling
              "flex w-full items-center justify-between text-left hover:no-underline transition-[color,text-decoration-line,background-color,border-color,box-shadow] duration-200",
              "font-medium hover:text-foreground focus:text-foreground",
              // Padding based on size
              size === "sm" && "px-3 py-2.5 text-sm",
              size === "default" && "px-4 py-3",
              size === "lg" && "px-6 py-4 text-lg font-semibold",
              // Variant-specific trigger styling (item-level hover가 있는 variant는 trigger hover 무효화)
              variant === "outline" && "hover:bg-transparent",
              variant === "ghost" && "hover:bg-transparent",
              variant === "contained" && "hover:bg-transparent",
              // Disabled state
              item.disabled && "opacity-50 cursor-not-allowed hover:text-muted-foreground"
            )}
          >
            {item.trigger}
          </UIAccordionTrigger>
          <UIAccordionContent
            className={cn(
              // Base content styling
              "text-muted-foreground",
              // Padding based on size
              size === "sm" && "px-3 pb-2.5 pt-0 text-xs",
              size === "default" && "px-4 pb-3 pt-0 text-sm",
              size === "lg" && "px-6 pb-4 pt-0 text-base",
              // Variant-specific content styling
              variant === "outline" && "bg-muted/10",
              variant === "contained" && "bg-muted/20"
            )}
          >
            <UIBox className="space-y-2">
              {item.content}
            </UIBox>
          </UIAccordionContent>
        </UIAccordionItem>
      ))}
    </UIAccordion>
  )
})

UIAccordionFAQ.displayName = "UIAccordionFAQ"

// ========================================
// Type Exports
// ========================================

export type UIAccordionProps = React.ComponentPropsWithoutRef<typeof UIAccordion>
export type UIAccordionItemProps = React.ComponentPropsWithoutRef<typeof UIAccordionItem>
export type UIAccordionTriggerProps = React.ComponentPropsWithoutRef<typeof UIAccordionTrigger>
export type UIAccordionContentProps = React.ComponentPropsWithoutRef<typeof UIAccordionContent>

